import { useResources } from '@funtech-int/rival-react';
import type { Source } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import {
    SeasonsMetadata,
    seasonsMetadataQuery,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createResourceQuery } from '@funtech-int/rival';

import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';

const SEASONS_METADATA_UPDATE_DELAY = 60 * 60 * 1000;

interface SeasonsMetadataParams {
    contentId: string;
    source?: Source;
}

interface SeasonsMetadataQueryContext {
    params: SeasonsMetadataParams;
    client: GraphqlClient;
}

const seasonsMetadataQueryResource = createResourceQuery<
    SeasonsMetadataQueryContext,
    SeasonsMetadata
>({
    key(ctx) {
        return [seasonsMetadataQuery.name, ctx.params.contentId].filter(Boolean).join(':');
    },
    async fetch(ctx) {
        const { data } = await ctx.client.fetch({
            operation: seasonsMetadataQuery,
            variables: {
                contentId: ctx.params.contentId,
                source: ctx.params.source,
            },
        });

        if (!data) {
            throw new Error('Failed to fetch seasons metadata');
        }

        return data;
    },
    toJSON(data: any) {
        return data;
    },
    hydrate(data: any) {
        return data;
    },
});

interface UseSeasonsMetadataQueryParams {
    id: string;
    source?: Source;
}

export function useSeasonsMetadataQuery({ id, source }: UseSeasonsMetadataQueryParams) {
    const client = useGraphqlClient();

    const [seasonsMetadata] = useResources(
        seasonsMetadataQueryResource(
            {
                client,
                params: {
                    contentId: id,
                    source,
                },
            },
            { lifetime: SEASONS_METADATA_UPDATE_DELAY }
        )
    );

    return {
        seasonsMetadata,
    };
}
